import { FC, ReactNode, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { IonButtons, IonContent } from '@ionic/react';
import { AllieBadge, AlliePortalTooltip, AllieText } from '../../../components';
import { AvatarInfo } from './components/AvatarInfo';
import {
  AllieHeaderStyled,
  ContentHeaderButton,
  HeaderButtonDesktop,
  HelpIconButton,
  InfoUserWrapper,
  StyledToolbar,
} from './components/Header.styles';
import { ListOptionsMenu } from './components/ListOptionsMenu';
import { SpectatorMode } from './components/SpectatorMode';
import { HeaderDesktopProps } from './Header.types';
import { AllieHelpCircleOutlineIcon } from '../../../styling/icons';
import { useScreen } from '../../../common/hooks';

const HEADER_TITLE = 'header-title-container';

export const AllieHeaderDesktop: FC<HeaderDesktopProps> = ({
  loggedUser,
  optionsMenuAvatar,
  color,
  isAuthenticated,
  textLogin,
  messageLogin,
  onClickLogin,
  currentTime,
  helpLabel,
  showHelp = true,
  onHelpClick,
  currentLanguage,
  onLanguageChange,
  languages,
  languageLabel,
  alerts,
  contentButtonMenu,
  contentActions,
  showAlerts = true,
}) => {
  const { isTabletHook, isMobileHook } = useScreen();

  const InfoUserDay = (
    <div style={{ textAlign: 'left' }}>
      {isAuthenticated && loggedUser && (
        <AllieText type="body2" color="neutral-70" weight={600}>
          {loggedUser.name}
        </AllieText>
      )}
      {currentTime && (
        <AllieText type="body3" color="neutral-70">
          {currentTime}
        </AllieText>
      )}
    </div>
  );

  return (
    <AllieHeaderStyled color={color}>
      <StyledToolbar>
        {contentButtonMenu ? (
          <IonButtons
            slot="start"
            style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}
          >
            {contentButtonMenu}
          </IonButtons>
        ) : null}
        <div id={HEADER_TITLE} />
        <InfoUserWrapper slot="end">
          {isAuthenticated && (
            <>
              {contentActions}
              {showAlerts && (
                <HeaderButtonDesktop
                  allieSize="xs"
                  allieType="subtle"
                  corner="rounded-edges-plus"
                  href={alerts.href}
                  data-testid="header-alerts-button"
                >
                  <ContentHeaderButton>
                    {!isTabletHook && (
                      <AllieText type="body2" color="neutral-70" weight={600}>
                        {alerts.label}
                      </AllieText>
                    )}
                    <AllieBadge
                      allieColor={alerts.unseenAlertsTotal ? 'error-main' : 'neutral-90'}
                      paddingInline="4px"
                      data-testid="header-alert-badge"
                    >
                      {alerts.unseenAlertsTotal}
                    </AllieBadge>
                  </ContentHeaderButton>
                </HeaderButtonDesktop>
              )}
              {showHelp && (
                <AlliePortalTooltip content={helpLabel ?? ''} side="bottom">
                  <HelpIconButton
                    id="intercom-button"
                    type="button"
                    data-testid="header-help-button"
                    aria-label={helpLabel}
                    onClick={onHelpClick}
                  >
                    <AllieHelpCircleOutlineIcon />
                  </HelpIconButton>
                </AlliePortalTooltip>
              )}
              {!(isTabletHook || isMobileHook) && InfoUserDay}
              <AvatarInfo optionsMenuAvatar={optionsMenuAvatar as any} loggedUser={loggedUser}>
                <IonContent>
                  <ListOptionsMenu
                    options={optionsMenuAvatar as any}
                    currentLanguage={currentLanguage}
                    onLanguageChange={onLanguageChange}
                    languages={languages}
                    languageLabel={languageLabel}
                  />
                </IonContent>
              </AvatarInfo>
            </>
          )}
          {!isAuthenticated && (
            <SpectatorMode
              onClick={onClickLogin}
              labels={{ login: textLogin, message: messageLogin }}
              platform={isTabletHook || isMobileHook ? 'tablet' : 'desktop'}
            />
          )}
        </InfoUserWrapper>
      </StyledToolbar>
    </AllieHeaderStyled>
  );
};

export default AllieHeaderDesktop;

export const AllieHeaderTitle: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const headerContainer = document.getElementById(HEADER_TITLE);

    if (headerContainer) {
      // Clear any previous content in the container
      if (!headerContainer.dataset.mounted) {
        headerContainer.innerHTML = ''; // Clear content
        headerContainer.dataset.mounted = 'true'; // Mark as mounted
        setContainer(headerContainer);
      }
    }

    return () => {
      if (headerContainer) {
        // Cleanup only once
        delete headerContainer.dataset.mounted; // Remove mounted flag
        headerContainer.innerHTML = '';
      }
    };
  }, []); // Run only once

  // If no container exists, render nothing
  if (!container) return null;

  // Portal content to the header container
  return ReactDOM.createPortal(children, container);
};
